import React from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, HpmsCustomContentCollapseEvent, HPMSOnOpen, setVisibility } from '@philips-emr/hpms-components';
import HpmsCustomContentCollapseApi from '@philips-emr/hpms-components/lib/components/hpms-custom-card/hpms-custom-content-collapse-api';
import { ActionAPI, Button, ContextualBarAPI, DeckAPI, DeckValue, PanelAPI, RepeaterAPI, Stamp, StampIcon, TabWithContentAPI } from '@philips-emr/tws-components/lib/components';
import { ButtonTheme, Color, Placement } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnDynamicContentRender, OnMount, OnNavigate, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { Data } from '@philips-emr/tws-core/lib/component/context/data-context';
import { string } from '@philips-emr/tws-core/lib/component/prop-types';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { DetalhesRequisicaoAliases as Aliases, FragmentNames } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { DownloadService, ReportService } from '../../services';
import BaseController from '../base/base-controller';

interface Requisicao {
  nrSeqItem: number,
  ieTipoItem: string,
  ieEstagio: number,
  ieTipoGuia: string,
  ieTipoAnexo: string;
}

interface Diagnosticos {
  [key: string]: Diagnostico | undefined,
}

interface Diagnostico {
  dsDoenca: string,
  cdDoenca: string,
}

enum Status {
  A = 'A',
  C = 'C',
  G = 'G',
  I = 'I',
  N = 'N',
  P = 'P',
  S = 'S',
  T = 'T',
  U = 'U',
}

@Controller({ fragment: FragmentNames.DETALHES_REQUISICAO })
export default class DetalhesRequisicaoController extends BaseController {

  @Inject(Aliases.PANEL_REQUISICAO)
  private panelRequisicao: PanelAPI;
  @Inject(Aliases.PANEL_CID)
  private panelCid: PanelAPI;
  @Inject(Aliases.PANEL_GLOSAS_OCORRENCIAS)
  private panelGlosasOcorrencias: PanelAPI;
  @Inject(Aliases.PANEL_PROCEDIMENTOS_MATERIAIS)
  private panelProcedimentosMateriais: PanelAPI;
  @Inject(Aliases.PANEL_ANEXOS)
  private panelAnexos: PanelAPI;
  @Inject(Aliases.TAB_DADOS_REQUISICAO)
  private tabDadosReq: TabWithContentAPI;
  @Inject(Aliases.TAB_ANEXOS_TISS)
  private tabAnexosTiss: TabWithContentAPI;
  @Inject(Aliases.CONTEXTUAL_BAR_0)
  private contextBarReq: ContextualBarAPI;
  @Inject(Aliases.ACTION_IMPRIMIR)
  private actionImprimir: ActionAPI;
  @Inject(Aliases.REPEATER_CID)
  private repeaterCID: RepeaterAPI;

  diagnosticos?: Diagnosticos;

  @OnMount(Aliases.LAYOUT_DETALHES_REQUISICAO)
  onMount(): void {
    const { nrSeqBeneficiario, nrSeqRequisicao } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqBeneficiario, nrSeqRequisicao, origem: 'requisicao', nrSeq: nrSeqRequisicao });
  }

  @OnMount(Aliases.PANEL_LOADER)
  onLoaderMount({ target }: MountEvent): void {
    const api = target as PanelAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();
    const { nrSeqRequisicao } = this.historyService.getCurrentRouteParams();
    void HttpService.get<boolean>(Endpoints.GET.CHECK_GUIA_TISS_REQUISICAO, { nrSeqBeneficiario, nrSeqRequisicao })
      .then(({ body }) => {
        api.setVisible(false);
        this.panelRequisicao.setVisible(true);
        if(body){
          this.tabAnexosTiss.setVisible(!!body);
          this.tabDadosReq.setVisible(!!body);
          this.contextBarReq.setTitle('');
          this.context.set({ hasTiss: true });
        } else {
          this.tabAnexosTiss.setVisible(!!body);
          this.tabDadosReq.setVisible(!!body);
        }
      }).catch(() => catchRenderException(() => {
        api.setVisible(false);
        this.panelRequisicao.setVisible(true);
        this.tabAnexosTiss.setVisible(false);
      }));
  }

  @OnNavigate(Aliases.TAB_CONTROL_REQUISICAO)
  onTabChange(): void {
    const { hasTiss } = this.context.getBulkData();
    if (hasTiss) {
      this.contextBarReq.setTitle('');
    }
  }

  @OnMount(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPSE)
  onMountCollapse({ target }: MountEvent): void {
    const api = target as unknown as HpmsCustomContentCollapseApi;
    const data = api.getData();
    const { ieTipoItem } = data as unknown as Requisicao;

    const endpoint = (ieTipoItem: string): string => ieTipoItem === 'P' ? Endpoints.GET.GLOSAS_ITEM_PROCEDIMENTO : Endpoints.GET.GLOSAS_ITEM_MATERIAL;
    api.setEndpoint(string(endpoint(ieTipoItem)));
  }

  @OnAfterFetch(Aliases.FETCH_REQUISICAO)
  onRequisicaoAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { ieEstagio, ieTipoGuia } = data as unknown as Requisicao;
      const { nrSeqRequisicao, nrSeqBeneficiario } = this.context.getBulkData();
      const imprimir = (ieEstagio: number): boolean => ieEstagio !== 2 ? false : true;

      if (imprimir(ieEstagio)) {
        void ReportService.print({
          cdGrupo: '12',
          nrSeqBeneficiario: nrSeqBeneficiario as number,
          endpoint: Endpoints.GET.RELATORIO_REQUISICAO,
          parametrosEndpoint: {
            nrSeqRequisicao: nrSeqRequisicao as number,
            ieTipoGuia,
          },
          parametrosRelatorio: {
            NR_SEQ_REQUISICAO: nrSeqRequisicao as number,
          },
        })
          .then(({ event }) => {
            this.actionImprimir.setEnabled(true);
            this.actionImprimir.onClick.listen(event);
          });
      }
    }
  }

  @OnAfterFetch(Aliases.FETCH_ANEXOS)
  onAnexosAfterFetxh = (event: FetchEvent): void => setVisibility(event, this.panelAnexos);

  @OnAfterFetch(Aliases.FETCH_CID)
  onCidAfterFetch = (event: FetchEvent): void => {
    setVisibility(event, this.panelCid);
    if (!event.data) {
      return;
    }
    this.setDataReapeater(event.data as Diagnosticos, this.repeaterCID, this.panelCid, 'diagnosticoRequisicaoAutorizacao');
  };

  private setDataReapeater(values: Diagnosticos, repeater: RepeaterAPI, panel: PanelAPI, keyCheck: string): void {
    if (isEmptyArrayOrObject(values)) {
      panel.setVisible(false);
      return;
    }

    const displayTexts: Data[] = [];

    values && Object.keys(values).forEach(key => {
      const displayObj: Data = this.resolveDisplayText(values && values[key]);

      if (displayObj[keyCheck]) {
        displayTexts.push(displayObj);
      }
    }
    );

    repeater.setData(displayTexts as Record<string, Diagnosticos>[]);
  }

  @OnAfterFetch(Aliases.FETCH_GLOSAS_OCORRENCIAS)
  onGlosasOcorrenciasAfterFetch = (event: FetchEvent): void => setVisibility(event, this.panelGlosasOcorrencias);

  @OnAfterFetch(Aliases.FETCH_PROCEDIMENTOS_MATERIAIS)
  onProcedimentosMateriaisAferFetch = (event: FetchEvent): void => setVisibility(event, this.panelProcedimentosMateriais);

  @OnSelectItem(Aliases.DECK_ANEXOS)
  onAnexosSelectItem({ item, target }: SelectItemEvent<DeckValue>): void {
    const api = target as DeckAPI;
    api.unselectAll();

    const { nrSeqBeneficiario, nrSeqRequisicao } = this.context.getBulkData();
    const { nmArquivo, nrSeqAnexo } = item;

    const parameters = {
      nrSeqAnexo,
      nrSeqBeneficiario,
      nrSeqRequisicao,
    };

    DownloadService.of({
      endpoint: Endpoints.GET.DOWNLOAD_REQUISICAO,
      name: nmArquivo as string,
      parameters,
    });
  }
  @HPMSOnOpen(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPSE)
  onOpen(event: HpmsCustomContentCollapseEvent<Data>): void {
    if (!event || !event.data) {
      return;
    }

    const { nrSeqBeneficiario, nrSeqRequisicao } = this.context.getBulkData();
    const { nrSeqItem, ieTipoItem } = event.data as unknown as Requisicao;

    const { onOpenRender } = event;
    onOpenRender({ parameters: { nrSeqBeneficiario, nrSeqRequisicao, nrSeqItem, ieTipoItem } });
  }

  private resolveDisplayText(diagnostico: Diagnostico | undefined): Data {
    if (!diagnostico) {
      return { diagnosticoRequisicaoAutorizacao: '' };
    }

    const { cdDoenca, dsDoenca } = diagnostico;
    return { diagnosticoRequisicaoAutorizacao: ` ${cdDoenca} - ${dsDoenca} ` };
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_STATUS)
  onDynamicContentRenderStamp({ data, render }: RenderEvent): void {
    const { ieStatus } = data as { ieStatus: Status };

    const props = {
      [Status.A]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING },
      [Status.C]: { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK },
      [Status.G]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED },
      [Status.I]: { className: 'c-roxo-stamp__stamp--color', icon: StampIcon.CANCELED_OUTLINE },
      [Status.N]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED },
      [Status.P]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.S]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.T]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK },
      [Status.U]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE },
    };

    render(() => <Stamp {...props[ieStatus]} label={`@beneficiary.status-item-requisicao/${ieStatus.toLowerCase()}`} />);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_ACTION)
  onDynamicContentRenderVisualizacao(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSeqItem, ieTipoAnexo } = data as unknown as Requisicao;

    if (!['OP', 'QU'].includes(ieTipoAnexo)) return;

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.RIGHT}
        onClick={() => this.openModalMaterial(nrSeqItem, ieTipoAnexo)}
        className="c-requisicao-item_item-button-action"
      />
    ));
  }

  private openModalMaterial(nrSeqMat: number, ieTipoAnexo: string): void {
    const { origem, nrSeqBeneficiario, nrSeqRequisicao } = this.context.getBulkData();

    ModalService.openDialog({
      title: 'beneficiary.detalhes-item',
      fragment: FragmentNames.MODAL_ITEM_TISS,
      contextData: {
        nrSeqBeneficiario,
        origem,
        nrSeqMat,
        ieTipoAnexo,
        nrSeqOrigem: nrSeqRequisicao,
      },
    });
  }
}
